"use client";

import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { AuthCard } from "@/components/auth/AuthCard";
import { SignupPromo, type SignupPromoProps } from "@/components/auth/SignupPromo";
import { useAuthModal } from "@/components/auth/AuthModalContext";

type AuthModalProps = {
  promo?: SignupPromoProps;
};

const AuthModal = ({ promo }: AuthModalProps) => {
  const { open, setOpen } = useAuthModal();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-sky-50 xl:inset-auto xl:left-1/2 xl:top-1/2 xl:block xl:overflow-visible xl:bg-transparent xl:-translate-x-1/2 xl:-translate-y-1/2 focus:outline-none"
        >
          <DialogTitle className="sr-only">Anmelden</DialogTitle>

          {/* Mobile close button — sits above the white card */}
          <DialogPrimitive.Close className="sticky top-0 self-end p-4 pr-6 transition-opacity hover:opacity-70 focus:outline-none xl:hidden z-10">
            <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none" aria-hidden="true">
              <path d="M1 21.9705L11.4853 11.4853M11.4853 11.4853L21.9705 1M11.4853 11.4853L1 1M11.4853 11.4853L21.9705 21.9705" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="sr-only">Schließen</span>
          </DialogPrimitive.Close>

          <div className="relative flex w-full flex-col xl:w-auto xl:flex-row xl:items-center xl:gap-20 xl:rounded-[32px] xl:bg-sky-50 xl:p-4">
            <AuthCard onSuccess={() => setOpen(false)} source="global_modal" />

            {promo && (
              <div className="w-full px-6 py-8 xl:hidden">
                <SignupPromo {...promo} />
              </div>
            )}

            {/* Desktop promo — side by side */}
            {promo && (
              <div className="hidden w-[559px] shrink-0 pr-10 xl:block">
                <SignupPromo {...promo} />
              </div>
            )}

            {/* Desktop close button — inside the card */}
            <DialogPrimitive.Close className="absolute right-8 top-8 hidden rounded-full p-2 transition-opacity hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-sky-75 xl:block">
              <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none" aria-hidden="true">
                <path d="M1 21.9705L11.4853 11.4853M11.4853 11.4853L21.9705 1M11.4853 11.4853L1 1M11.4853 11.4853L21.9705 21.9705" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="sr-only">Schließen</span>
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export { AuthModal };
